import { useTranslations } from "next-intl";
import { useState, memo } from "react";
import classNames from "classnames";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { useCurrentUser, useSizeReferenceDisplay } from "@hooks/use-current-user";
import { useReloadUser } from "@hooks/use-modify-user";
import { useFiltersList } from "@hooks/use-features";
import { useFilterRecentlyModified } from "@hooks/use-catalog";
import InputButtonsSameSize from "@components/core/InputButtonsSameSize";
import Button from "@components/core/Button";
import Link from "@components/core/Link";
import SizeReferenceToggle from "@components/views/Profile/ManageSizes/shared/SizeReferenceToggle";
import { useSizeReferenceAnimation } from "@hooks/use-size-reference-animation";
import shouldHideFilterSectionInListingPage from "@utils/catalog-filters/shouldHideFilterSectionInListingPage";
import { putUserFilterSizes, putUserSizeReference } from "@utils/call-api/users-call";
import SectionButton from "./Elements/SectionButton";
import SectionContent from "./Elements/SectionContent";

const filterSectionKey = "sizes";

const SizesSection = memo(function SizesSection({
  listingPage,
  onClose,
  filterSectionOpened,
  setFilterSectionOpened,
  sizeItems,
  refineSize,
}) {
  const t = useTranslations("catalog");

  const user = useCurrentUser();
  const reloadUser = useReloadUser();
  const filtersList = useFiltersList();
  const filterRecentlyModified = useFilterRecentlyModified();
  const userFilterSizes = user?.size_filters;
  const sizeReference = useSizeReferenceDisplay();

  const [loading, setLoading] = useState(false);
  const {
    selectedSizeReference,
    visibleSizeReference,
    animPhase,
    handleSizeReferenceChange,
    resetSizeReference,
  } = useSizeReferenceAnimation();

  if (shouldHideFilterSectionInListingPage(listingPage, "sizes")) return null;
  if (sizeItems.length < 2) return null;

  const sectionTitle = t(`filters.section_titles.${filterSectionKey}`);

  const checkedFilters = {
    sizes: sizeItems
      .filter((sizeItem) => sizeItem.isRefined && sizeItem.value !== "TU")
      .map((sizeItem) => sizeItem.value),
  };

  function handleSizeClick(optionValue) {
    const eventData = { button: "filter", filter: "sizes", value: optionValue };
    ahoy("$click", eventData);
    analytics.track("$click", eventData);

    filterRecentlyModified(filterSectionKey);

    refineSize(optionValue);
  }

  function handleClose() {
    resetSizeReference(sizeReference);
    onClose();
  }

  function handleGoBack() {
    resetSizeReference(sizeReference);
    setFilterSectionOpened("");
  }

  async function updateUserSizesPreferences() {
    try {
      setLoading(true);

      const newUserSizes = checkedFilters.sizes;
      const sizeReferenceCall = () => {
        if (selectedSizeReference === sizeReference) return Promise.resolve();

        return putUserSizeReference(selectedSizeReference);
      };

      await Promise.all([putUserFilterSizes({ sizes: newUserSizes }), sizeReferenceCall()]);
      await reloadUser();
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }

  function displayUserFilterSizes() {
    if (!user) return null;

    return (
      <div className="p-3 bg-white border-bottom">
        <div className="d-flex justify-content-between mb-3">
          <p className="mb-0">{t("filters.sizes.profile.title")}</p>
          <Link
            href="/profile/manage-sizes"
            className="link-primary no-underline"
          >
            {userFilterSizes.length
              ? t("filters.sizes.profile.see_profile")
              : t("filters.sizes.profile.complete_profile")}
          </Link>
        </div>
        <div>
          <p className="mb-0 text-gray">
            {userFilterSizes.length
              ? userFilterSizes.map((size) => t(`filters.sizes.profile.sizes.${size}`)).join(", ")
              : "- -"}
          </p>
        </div>
      </div>
    );
  }

  function displaySizesFiltersOptions() {
    let sizesFiltersList = filtersList.sizes.map((filter) => {
      const sizeItem = sizeItems.find((item) => item.value === filter);

      if (!sizeItem) return;

      return {
        title: t(`filters.sizes.${visibleSizeReference}.${filter}`),
        value: sizeItem.value,
      };
    });
    sizesFiltersList = sizesFiltersList.filter((sizeFilter) => sizeFilter);

    if (!sizesFiltersList.length) return null;

    return (
      <div className={classNames("size-filter-buttons", animPhase)}>
        <InputButtonsSameSize
          options={sizesFiltersList}
          variant="light-gray"
          selectedValues={checkedFilters.sizes}
          onClick={(optionValue) => handleSizeClick(optionValue)}
          containerClassName="d-flex align-items-center flex-wrap gap-2"
          withTracking={false}
        />
      </div>
    );
  }

  function displaySizeReferenceToggle() {
    if (!user) return null;

    return (
      <div className="mt-4 d-flex flex-column gap-2 justify-content-center">
        <p className="mb-0">{t("filters.sizes.size_reference.title")}</p>
        <SizeReferenceToggle
          name="size-section-size-reference-toggle"
          variant="lighter-gray"
          sizeReference={selectedSizeReference}
          onChange={handleSizeReferenceChange}
        />
      </div>
    );
  }

  function displayDifferentSizesWarning() {
    if (!user) return null;

    const userFilterSizesOrdered = userFilterSizes.sort().join("/");
    const checkedFiltersOrdered = checkedFilters.sizes.sort().join("/");

    if (userFilterSizesOrdered === checkedFiltersOrdered && selectedSizeReference === sizeReference)
      return null;

    return (
      <div className="mt-4 d-flex flex-column gap-2 justify-content-center">
        <p className="mb-0 text-center font-size-sm text-gray">
          {t("filters.sizes.profile.different_sizes_warning")}
        </p>

        <Button
          variant="link-primary"
          className="no-underline"
          size="sm"
          onClick={updateUserSizesPreferences}
          loading={loading}
        >
          {t("filters.sizes.profile.update_sizes_preferences")}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <SectionButton
        checkedFilters={checkedFilters}
        filterTypesToDisplay={["sizes"]}
        title={sectionTitle}
        onClick={() => setFilterSectionOpened(filterSectionKey)}
      />

      <SectionContent
        isOpen={filterSectionOpened === filterSectionKey}
        onClose={handleClose}
        onGoBack={handleGoBack}
        title={sectionTitle}
      >
        {displayUserFilterSizes()}

        <div className="p-3 bg-white">
          {displaySizesFiltersOptions()}
          {displaySizeReferenceToggle()}

          {displayDifferentSizesWarning()}
        </div>
      </SectionContent>
    </div>
  );
});

export default SizesSection;
